import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import PageHeader from './PageHeader';
import PaymentFailureBanner from '../billing/PaymentFailureBanner';
import OfflineBanner from '../common/OfflineBanner';
import FeedbackWidget from '../feedback/FeedbackWidget';
import apiClient, {
  getAuthRefreshState,
  retrySessionRefresh,
  subscribeAuthRefreshState,
} from '../../api/client';
import { useAppSelector } from '../../store/hooks';
import {
  buildLoginPathFromContext,
  getAuthReturnMeta,
  isChunk329AuthRecoveryRoute,
  readStoredAuthReturnContext,
  resolveAuthReturnRouteId,
} from '../../lib/authReturnContext';

interface PageShellProps {
  children: React.ReactNode;
}

type BannerStatus = 'past_due' | 'canceled' | null;

const BANNER_DISMISS_KEY = 'payment_banner_dismissed_session';

const PageShell: React.FC<PageShellProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAppSelector((s) => s.auth);
  const [bannerStatus, setBannerStatus] = useState<BannerStatus>(null);
  const [gracePeriodEndsAt, setGracePeriodEndsAt] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [authRefreshState, setAuthRefreshState] = useState(getAuthRefreshState);
  const [retryingSession, setRetryingSession] = useState(false);

  useEffect(() => subscribeAuthRefreshState(setAuthRefreshState), []);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    // Reset dismiss on new session (sessionStorage survives tab navigation, not new sessions)
    const dismissed = sessionStorage.getItem(BANNER_DISMISS_KEY);
    if (dismissed) {
      setBannerDismissed(true);
      return;
    }

    const fetchSubStatus = async () => {
      try {
        const res = await apiClient.get<{
          status?: string;
          gracePeriodEndsAt?: string;
        }>('/billing/subscription');
        const { status, gracePeriodEndsAt: graceEnd } = res.data;
        if (status === 'past_due') {
          setBannerStatus('past_due');
          setGracePeriodEndsAt(graceEnd ?? null);
        } else if (status === 'canceled') {
          setBannerStatus('canceled');
        }
      } catch {
        // non-blocking — billing API may not exist yet (Chunk 48 backend)
      }
    };

    fetchSubStatus();
  }, [isAuthenticated, user?.id]);

  const handleBannerDismiss = () => {
    sessionStorage.setItem(BANNER_DISMISS_KEY, '1');
    setBannerDismissed(true);
  };

  const handleRetrySession = async () => {
    setRetryingSession(true);
    await retrySessionRefresh();
    setRetryingSession(false);
  };

  const showSessionBanner =
    isAuthenticated &&
    authRefreshState.status !== 'idle' &&
    authRefreshState.message;
  const currentRouteId = resolveAuthReturnRouteId(location.pathname);
  const storedAuthReturnContext = readStoredAuthReturnContext();
  const matchingStoredAuthContext =
    storedAuthReturnContext?.routeId === currentRouteId
      ? storedAuthReturnContext
      : null;
  const authReturnMeta = getAuthReturnMeta(matchingStoredAuthContext);
  const shouldDemoteSignInBannerAction = isChunk329AuthRecoveryRoute(location.pathname);
  const handleResumeSignIn = () => {
    const latestContext = readStoredAuthReturnContext();
    const resumePath =
      latestContext?.routeId === currentRouteId
        ? buildLoginPathFromContext(latestContext)
        : '/login';

    navigate(resumePath);
  };

  return (
    <div className="flex h-screen bg-app overflow-hidden">
      <Sidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <PageHeader onMobileMenuOpen={() => setMobileNavOpen(true)} />
        <OfflineBanner />

        {/* Payment failure banner — shown when subscription is past_due or canceled */}
        {bannerStatus && !bannerDismissed && (
          <PaymentFailureBanner
            subscriptionStatus={bannerStatus}
            gracePeriodEndsAt={gracePeriodEndsAt}
            onDismiss={handleBannerDismiss}
          />
        )}

        <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 animate-fade-in">
            {showSessionBanner && authRefreshState.status === 'retrying' && (
              <div
                role="status"
                aria-live="polite"
                className="mb-6 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-300"
              >
                <div className="flex items-center gap-3">
                  <svg className="h-4 w-4 animate-spin flex-shrink-0" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <span>{authRefreshState.message}</span>
                </div>
              </div>
            )}

            {showSessionBanner && authRefreshState.status === 'degraded' && (
              <div
                role="alert"
                className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <p>{authRefreshState.message}</p>
                  <button
                    type="button"
                    onClick={() => void handleRetrySession()}
                    disabled={retryingSession}
                    className="btn btn--primary shrink-0 disabled:opacity-50"
                  >
                    {retryingSession ? 'Retrying…' : 'Retry now'}
                  </button>
                </div>
              </div>
            )}

            {showSessionBanner && authRefreshState.status === 'sign_in_required' && (
              <div
                role="alert"
                className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-5 py-4 text-sm text-red-200"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-red-100">
                      {authReturnMeta ? `Resume ${authReturnMeta.routeName}` : 'Sign-in required'}
                    </h2>
                    <p className="mt-1">{authRefreshState.message}</p>
                    {shouldDemoteSignInBannerAction && authReturnMeta ? (
                      <p className="mt-1 text-xs text-red-100/80">
                        {authReturnMeta.proofLabel
                          ? `${authReturnMeta.proofLabel} stays mounted while Pinecurl waits to resume ${authReturnMeta.ownerCtaLabel}.`
                          : `${authReturnMeta.ownerCtaLabel} stays mounted on ${authReturnMeta.routeLabel} while Pinecurl waits for sign-in recovery.`}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={handleResumeSignIn}
                    className={`btn ${shouldDemoteSignInBannerAction ? 'btn--ghost' : 'btn--primary'} shrink-0`}
                  >
                    {authReturnMeta?.loginButtonLabel ?? 'Sign in again'}
                  </button>
                </div>
              </div>
            )}

            {children}
          </div>
          {/* Compliance footer — always visible in the app shell */}
          <div className="hidden md:flex items-center justify-center gap-6 py-3 text-xs text-gray-600 border-t border-border/40">
            <span className="text-yellow-700/80">⚠️ Not financial advice — for informational purposes only</span>
            <span>·</span>
            <Link to="/disclaimer" className="hover:text-gray-400 transition-colors">Disclaimer</Link>
            <Link to="/privacy" className="hover:text-gray-400 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-400 transition-colors">Terms</Link>
          </div>
        </main>

        <BottomNav />
        <FeedbackWidget />
      </div>
    </div>
  );
};

export default PageShell;
